import React, { useMemo, useState } from 'react';

import PublicIcon from '@mui/icons-material/Public';
import { Box, Stack, Typography } from '@mui/material';

import FormVirtualizedAutocomplete from '@/components/NewUI/Components/CustomUIElements/FormVirtualizedAutocomplete';
import { Button } from '@/components/NewUI/ComponentsLibrary';
import timeZoneNames from '@/components/NewUI/utils/timeZoneNames.json';

import { offsetLabel, timeZoneLabel, zoneCity } from './scheduling';
import { styles } from './styles';
import { aiButtonSx } from '../aiStyles';

interface IProps {
  timeZone: string;
  detectedTimeZone: string;
  // Instant the labels resolve against (AEDT vs AEST).
  referenceIso: string;
  onChange: (timeZone: string) => void;
}

// Detected from the device, changeable with the same time zone field as
// Working hours (ScoutCalendar/TeamAvailability), but labelled for people:
// "Sydney (AEDT, GMT+11:00)", never "Australia/Sydney".
const TimeZoneControl = ({ timeZone, detectedTimeZone, referenceIso, onChange }: IProps) => {
  const [editing, setEditing] = useState(false);

  const options = useMemo(() => {
    const zones = timeZoneNames.includes(timeZone) ? timeZoneNames : [...timeZoneNames, timeZone];
    return zones
      .map((zone) => ({ value: zone, label: timeZoneLabel(zone, referenceIso) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [referenceIso, timeZone]);

  return (
    <Stack sx={{ rowGap: 1.5 }}>
      {/* One line on wide screens; on phones the hint and Change wrap under
          the zone, lined up with the text rather than the globe. */}
      <Stack sx={styles.timeZoneRow}>
        <PublicIcon aria-hidden />
        <Box sx={styles.timeZoneText}>
          <Typography sx={styles.caption}>
            {'Times shown in '}
            <Typography component="span" sx={styles.strong}>
              {`${zoneCity(timeZone)} time (${offsetLabel(timeZone, referenceIso)})`}
            </Typography>
          </Typography>
          <Stack sx={styles.timeZoneActions}>
            <Typography aria-hidden component="span" sx={styles.timeZoneLeadDot}>
              ·
            </Typography>
            <Typography component="span" sx={styles.timeZoneHint}>
              {timeZone === detectedTimeZone ? 'Detected from your device' : 'Changed by you'}
            </Typography>
            <Typography aria-hidden component="span" sx={styles.timeZoneHint}>
              ·
            </Typography>
            <Button
              aria-controls={editing ? 'booking-time-zone-field' : undefined}
              aria-expanded={editing}
              buttonColor="purple"
              buttonVariant="text"
              id="booking-change-time-zone"
              onClick={() => setEditing((open) => !open)}
              size="small"
              sx={[styles.timeZoneButton, aiButtonSx]}
            >
              {editing ? 'Done' : 'Change'}
            </Button>
          </Stack>
        </Box>
      </Stack>
      {editing ? (
        <Box id="booking-time-zone-field" sx={styles.timeZoneField}>
          <FormVirtualizedAutocomplete
            disableClearable
            fullWidth
            dataTestId="booking-time-zone"
            disablePortal={false}
            label="Your time zone"
            passOptions={options}
            value={timeZone}
            setValue={(value: string) => {
              if (!value) return;
              onChange(value);
              setEditing(false);
            }}
          />
        </Box>
      ) : null}
    </Stack>
  );
};

export default TimeZoneControl;
